import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import PolymorphicAlertOverlay from '../components/PolymorphicAlertOverlay';
import { Link } from 'react-router-dom';

const NEWSLETTER_RECIPIENT_SCOPE = {
  schoolName: 'Scale Leadership Academy East',
  role: 'Student',
  periodName: '2025-2026',
  associationType: 'Vendor - FA - SLA',
};

/** Without ViewAllAccounts, recipient rows stay hidden until search has a term this long (privacy). */
const RECIPIENT_SEARCH_MIN_CHARS = 3;

function recipientSearchMeetsMinLength(rawSearch) {
  return rawSearch
    .split(/[\n,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .some((t) => t.length >= RECIPIENT_SEARCH_MIN_CHARS);
}

function userIsStudentInTerm(user, periodId) {
  if (!periodId) return false;
  return Boolean(
    user.accesscards?.some(
      (ac) =>
        ac.role === NEWSLETTER_RECIPIENT_SCOPE.role &&
        ac.school_name === NEWSLETTER_RECIPIENT_SCOPE.schoolName &&
        !ac.inactive &&
        ac.enrollments?.some((en) => en.period_id === periodId)
    )
  );
}

const Newsletter = ({ userData }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    variant: 'info',
    message: '',
    title: null,
    closeOnOverlayClick: true,
    showCloseButton: true,
    autoCloseMs: null,
    closeOnEscape: true,
  });
  const [showSelectedRecipients, setShowSelectedRecipients] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    allowMainResponse: true,
    allowCommentResponse: true,
    notifyMeOnResponses: true,
    allowAssigneesViewResponses: true,
    initialNotification: true
  });
  const [userGroups, setUserGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [filterToGroups, setFilterToGroups] = useState(false);
  const [filterToStudents, setFilterToStudents] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupTitle, setEditGroupTitle] = useState('');
  const [editGroupAccounts, setEditGroupAccounts] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [editGroupSearchTerm, setEditGroupSearchTerm] = useState('');
  const [filteredEditUsers, setFilteredEditUsers] = useState([]);
  const [updatingGroup, setUpdatingGroup] = useState(false);
  const [studentPeriods, setStudentPeriods] = useState([]);
  const [studentAssociations, setStudentAssociations] = useState([]);
  const [associationFilter, setAssociationFilter] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [deployPanelExpanded, setDeployPanelExpanded] = useState(false);
  const [canViewAllAccounts, setCanViewAllAccounts] = useState(false);
  const [canViewStudentsFilter, setCanViewStudentsFilter] = useState(false);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
  /** Blocks the page until reload when core recipient data fails to load. */
  const [criticalLoadError, setCriticalLoadError] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=newsletter', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setCanViewAllAccounts(Boolean(res.data.capabilities?.ViewAllAccounts));
          setCanViewStudentsFilter(Boolean(res.data.capabilities?.ViewStudentsFilter));
          setCapabilitiesLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setCanViewAllAccounts(false);
          setCanViewStudentsFilter(false);
          setCapabilitiesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const appliedRecipientScopeDefault = useRef(false);
  /** Once: users with ViewAllAccounts default to My Groups on; others start with no filter (search-all with privacy gate). */
  useEffect(() => {
    if (!capabilitiesLoaded || appliedRecipientScopeDefault.current) return;
    appliedRecipientScopeDefault.current = true;
    if (canViewAllAccounts) {
      setFilterToGroups(true);
      setFilterToStudents(false);
    }
  }, [capabilitiesLoaded, canViewAllAccounts]);

  useEffect(() => {
    if (!capabilitiesLoaded || canViewStudentsFilter) return;
    if (filterToStudents) {
      setFilterToGroups(false);
      setFilterToStudents(false);
      setAssociationFilter('');
    }
  }, [capabilitiesLoaded, canViewStudentsFilter, filterToStudents]);

  const closeAlert = useCallback(() => {
    setAlertConfig((prev) => ({ ...prev, open: false }));
  }, []);

  const openAlert = useCallback((opts) => {
    setAlertConfig({
      open: true,
      variant: 'info',
      message: '',
      title: null,
      closeOnOverlayClick: true,
      showCloseButton: true,
      autoCloseMs: null,
      closeOnEscape: true,
      ...opts,
    });
  }, []);

  // Load all users, groups, periods, and associations on component mount
  useEffect(() => {
    loadUsers();
    loadGroups();
    loadStudentPeriodsAndAssociations();
  }, []);

  // Filter users for edit group modal
  useEffect(() => {
    let filtered = allUsers;
    
    if (editGroupSearchTerm.trim()) {
      // Split search terms by newlines, commas, semicolons, or spaces
      const searchTerms = editGroupSearchTerm
        .split(/[\n,;]+/)
        .map(term => term.trim().toLowerCase())
        .filter(term => term.length > 0);
      
      if (searchTerms.length > 0) {
        filtered = filtered.filter(user => {
          // Create searchable text from user data
          const searchableText = [
            user.first_name || '',
            user.last_name || '',
            user.primary_email || '',
            ...(user.accesscards?.map(ac => 
              [ac.email || '', ac.role || '', ac.school_name || ''].join(' ')
            ) || [])
          ].join(' ').toLowerCase();
          
          // Check if any search term matches
          return searchTerms.some(term => searchableText.includes(term));
        });
      }
    }
    
    setFilteredEditUsers(filtered);
  }, [editGroupSearchTerm, allUsers]);

  useEffect(() => {
    if (filterToGroups && userGroups.length > 0 && !groupFilter) {
      setGroupFilter(String(userGroups[0].id));
    }
  }, [filterToGroups, userGroups, groupFilter]);

  useEffect(() => {
    const period = studentPeriods.find(
      (p) => p.name === NEWSLETTER_RECIPIENT_SCOPE.periodName
    );
    const periodId = period?.period_id;

    const searchTerms = searchTerm
      .split(/[\n,;]+/)
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term.length > 0);

    const matchesSearch = (user) => {
      if (searchTerms.length === 0) return false;
      const searchableText = [
        user.first_name || '',
        user.last_name || '',
        user.primary_email || '',
        ...(user.accesscards?.map((ac) =>
          [ac.email || '', ac.role || '', ac.school_name || ''].join(' ')
        ) || []),
      ]
        .join(' ')
        .toLowerCase();
      return searchTerms.some((term) => searchableText.includes(term));
    };

    const groupMembers = (gid) => {
      const g = userGroups.find((x) => String(x.id) === String(gid));
      return g?.accounts || [];
    };

    const studentsScoped = (() => {
      let list = allUsers.filter((u) => userIsStudentInTerm(u, periodId));
      if (associationFilter && periodId) {
        list = list.filter((user) =>
          user.accesscards?.some(
            (card) =>
              card.role === NEWSLETTER_RECIPIENT_SCOPE.role &&
              card.school_name === NEWSLETTER_RECIPIENT_SCOPE.schoolName &&
              card.enrollments?.some(
                (enrollment) =>
                  String(enrollment.association_id) === String(associationFilter) &&
                  enrollment.period_id === periodId
              )
          )
        );
      }
      return list;
    })();

    const groupsScoped = (() => {
      if (!groupFilter) return [];
      const ids = groupMembers(groupFilter);
      return allUsers.filter((u) => ids.includes(u.account_id));
    })();

    const modeScopedUsers = filterToStudents
      ? studentsScoped
      : filterToGroups
        ? userGroups.length === 0
          ? canViewAllAccounts
            ? allUsers
            : []
          : groupsScoped
        : allUsers;

    let filtered = modeScopedUsers;
    if (searchTerms.length > 0) {
      filtered = modeScopedUsers.filter(matchesSearch);
    }

    const noRecipientFilterSelected = !filterToGroups && !filterToStudents;
    const searchOk = recipientSearchMeetsMinLength(searchTerm);
    if (
      (!capabilitiesLoaded || !canViewAllAccounts) &&
      noRecipientFilterSelected &&
      !searchOk
    ) {
      filtered = [];
    }

    setFilteredUsers(filtered);
  }, [
    searchTerm,
    groupFilter,
    associationFilter,
    filterToGroups,
    filterToStudents,
    allUsers,
    userGroups,
    studentPeriods,
    capabilitiesLoaded,
    canViewAllAccounts,
  ]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/user-management/accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.accounts || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setCriticalLoadError({
        message: 'Accounts could not be loaded. Reload this page to continue.',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/groups', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      setCriticalLoadError({
        message: 'Groups could not be loaded. Reload this page to continue.',
      });
    }
  };

  const loadStudentPeriodsAndAssociations = async () => {
    try {
      const token = localStorage.getItem('token');
      const [periodsRes, associationsRes] = await Promise.all([
        axios.get('/api/user-management/periods', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/user-management/associations', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStudentPeriods(periodsRes.data.periods || []);
      const assocs = associationsRes.data.associations || [];
      setStudentAssociations(
        assocs.filter((a) => a.type === NEWSLETTER_RECIPIENT_SCOPE.associationType)
      );
    } catch (error) {
      console.error('Error loading periods/associations:', error);
      setCriticalLoadError({
        message:
          'Enrollment terms or associations could not be loaded. Reload this page to continue.',
      });
    }
  };

  const createGroup = async () => {
    if (!newGroupTitle.trim()) {
      openAlert({
        variant: 'warning',
        message: 'Please enter a group title.',
        autoCloseMs: 5000,
      });
      return;
    }

    if (selectedRecipients.length === 0) {
      openAlert({
        variant: 'warning',
        message: 'Please select at least one recipient to create a group.',
        autoCloseMs: 5000,
      });
      return;
    }

    setCreatingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/groups', {
        title: newGroupTitle.trim(),
        accounts: selectedRecipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      openAlert({
        variant: 'success',
        message: 'Group created successfully!',
        autoCloseMs: 3500,
      });
      setNewGroupTitle('');
      setShowCreateGroupModal(false);
      await loadGroups(); // Reload groups to include the new one
    } catch (error) {
      console.error('Error creating group:', error);
      openAlert({
        variant: 'error',
        message: 'Error creating group. Please try again.',
        autoCloseMs: 6500,
      });
    } finally {
      setCreatingGroup(false);
    }
  };

  const editGroup = async () => {
    if (!editGroupTitle.trim()) {
      openAlert({
        variant: 'warning',
        message: 'Please enter a group title.',
        autoCloseMs: 5000,
      });
      return;
    }

    setUpdatingGroup(true);
    openAlert({
      variant: 'loading',
      message: 'Updating group...',
      closeOnOverlayClick: false,
      showCloseButton: false,
      autoCloseMs: null,
      closeOnEscape: false,
    });

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/groups/${editingGroup.id}`, {
        title: editGroupTitle.trim(),
        accounts: editGroupAccounts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowEditGroupModal(false);
      setEditingGroup(null);
      setEditGroupTitle('');
      setEditGroupAccounts([]);
      await loadGroups(); // Reload groups
      setUpdatingGroup(false);

      openAlert({
        variant: 'success',
        message: 'Group updated successfully!',
        autoCloseMs: 2200,
        closeOnOverlayClick: true,
        showCloseButton: true,
        closeOnEscape: true,
      });
    } catch (error) {
      console.error('Error updating group:', error);
      setUpdatingGroup(false);
      openAlert({
        variant: 'error',
        message: error.response?.data?.error || 'Error updating group. Please try again.',
        autoCloseMs: 5000,
        closeOnOverlayClick: true,
        showCloseButton: true,
        closeOnEscape: true,
      });
    }
  };

  const deleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/groups/${groupToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      openAlert({
        variant: 'success',
        message: 'Group deleted successfully!',
        autoCloseMs: 3200,
      });
      setShowDeleteConfirmModal(false);
      setGroupToDelete(null);
      await loadGroups(); // Reload groups
    } catch (error) {
      console.error('Error deleting group:', error);
      openAlert({
        variant: 'error',
        message: 'Error deleting group. Please try again.',
        autoCloseMs: 6500,
      });
    }
  };

  const openEditGroupModal = (group) => {
    setEditingGroup(group);
    setEditGroupTitle(group.title);
    setEditGroupAccounts(group.accounts || []);
    setEditGroupSearchTerm('');
    setShowEditGroupModal(true);
  };

  const openDeleteConfirmModal = (group) => {
    setGroupToDelete(group);
    setShowDeleteConfirmModal(true);
  };

  const handleEditGroupAccountToggle = (accountId) => {
    setEditGroupAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleRecipientToggle = (accountId) => {
    setSelectedRecipients(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      openAlert({
        variant: 'warning',
        message: 'Please enter a title for your newsletter post.',
        autoCloseMs: 5000,
      });
      return;
    }
    
    if (!content.trim() || content === '<p><br></p>') {
      openAlert({
        variant: 'warning',
        message: 'Please enter some content for your newsletter post.',
        autoCloseMs: 5000,
      });
      return;
    }

    if (selectedRecipients.length === 0) {
      openAlert({
        variant: 'warning',
        message: 'Please select at least one recipient.',
        autoCloseMs: 5000,
      });
      return;
    }

    setSubmitting(true);
    openAlert({
      variant: 'loading',
      title: 'Sending newsletter',
      message: 'Creating your post and notifying recipients…',
      closeOnOverlayClick: false,
      showCloseButton: false,
      autoCloseMs: null,
      closeOnEscape: false,
    });

    try {
      const token = localStorage.getItem('token');
      const postData = {
        title: title,
        content: content,
        type: 'Post',
        relevantRole: 'Poster',
        reference_id: 'self', // Using 'self' for newsletter posts
        table: 'newsletter',
        account: userData?.account_id,
        assignees: selectedRecipients,
        settings: settings // Include the settings in the post data
      };

      await axios.post('/api/comments', postData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      openAlert({
        variant: 'success',
        title: 'Newsletter sent',
        message: 'Your post was created and notifications were sent to recipients.',
        autoCloseMs: 4500,
      });
      setTitle('');
      setContent('');
      setSelectedRecipients([]);
      setSearchTerm('');

    } catch (error) {
      console.error('Error creating newsletter post:', error);
      openAlert({
        variant: 'error',
        message: 'Error creating newsletter post. Please try again.',
        autoCloseMs: 7000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getUserDisplayName = (user) => {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const email = user.primary_email || user.accesscards?.[0]?.email || '';
    const role = user.accesscards?.[0]?.role || '';
    const school = user.accesscards?.[0]?.school_name || '';
    
    return {
      name: name || 'Unknown Name',
      email,
      role,
      school
    };
  };

  const isRecipientSelected = (accountId) => {
    return selectedRecipients.includes(accountId);
  };

  const copyEmailsToClipboard = (accountIds, users) => {
    const emails = accountIds
      .map(id => {
        const user = users.find(u => u.account_id === id);
        return user ? (user.primary_email || user.accesscards?.[0]?.email || '') : '';
      })
      .filter(Boolean);
    const text = emails.join('\n');
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(''), 2000);
      }).catch(() => { setCopyFeedback('Failed'); setTimeout(() => setCopyFeedback(''), 2000); });
    }
  };

  /** Toggle filters on/off; only one narrow filter active at a time. Off = search all accounts (with privacy gate if restricted). */
  const toggleRecipientFilter = (mode) => {
    if (mode === 'groups') {
      if (filterToGroups) {
        setFilterToGroups(false);
        setGroupFilter('');
        setAssociationFilter('');
      } else {
        setFilterToGroups(true);
        setFilterToStudents(false);
        setAssociationFilter('');
      }
    } else if (mode === 'students') {
      if (!canViewStudentsFilter) return;
      if (filterToStudents) {
        setFilterToStudents(false);
        setAssociationFilter('');
      } else {
        setFilterToStudents(true);
        setFilterToGroups(false);
        setGroupFilter('');
      }
    }
  };

  const handleSelectAll = () => {
    // Add filtered users to existing selection, avoiding duplicates
    setSelectedRecipients(prev => {
      const newSelection = [...prev];
      filteredUsers.forEach(user => {
        if (!newSelection.includes(user.account_id)) {
          newSelection.push(user.account_id);
        }
      });
      return newSelection;
    });
  };

  const handleDeselectAll = () => {
    // Remove only filtered users from selection, keep others
    setSelectedRecipients(prev => 
      prev.filter(id => !filteredUsers.some(user => user.account_id === id))
    );
  };

  const handleSettingsClick = () => {
    setShowSettings(!showSettings);
  };

  const sendBlocked =
    submitting ||
    !title.trim() ||
    !content.trim() ||
    content === '<p><br></p>' ||
    selectedRecipients.length === 0;

  const renderRecipientSelector = (usersListExtraStyle = {}, options = {}) => {
    const { hideSectionTitle = false } = options;
    return (
    <div style={styles.recipientsSection}>
      {!hideSectionTitle && (
        <div style={styles.recipientsHeader}>
          <label style={styles.label}>Select Recipients</label>
        </div>
      )}

      <div style={styles.recipientSelector}>
        <div style={styles.recipientFilterShell}>
          <label style={styles.nlSearchLabel} htmlFor="newsletter-recipient-search">
            Search
            <span style={styles.nlSearchHint}>
              {!capabilitiesLoaded
                ? ''
                : !canViewAllAccounts
                  ? filterToGroups || filterToStudents
                    ? ' (searching within the selected filter)'
                    : ` (no filter — ${RECIPIENT_SEARCH_MIN_CHARS}+ characters to show matches)`
                  : filterToGroups || filterToStudents
                    ? ' (searching within active filters)'
                    : ' (no filter — full list; turn on a filter to narrow)'}
            </span>
          </label>
          <textarea
            id="newsletter-recipient-search"
            placeholder={
              capabilitiesLoaded && !canViewAllAccounts
                ? `Type at least ${RECIPIENT_SEARCH_MIN_CHARS} characters — emails, first or last names (one per line)`
                : 'Enter emails, first names, or last names (one per line)'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchTextarea}
            rows={2}
          />

          <div style={styles.nlFilterToRow}>
            <span style={styles.nlFilterToPrefix} id="nl-recipient-scope-label">
              Filter
            </span>
            <div
              style={styles.nlFilterTagGroup}
              role="group"
              aria-labelledby="nl-recipient-scope-label"
            >
              {(capabilitiesLoaded
                ? [
                    { mode: 'groups', label: 'My Groups' },
                    ...(canViewStudentsFilter ? [{ mode: 'students', label: 'Students' }] : []),
                  ]
                : []
              ).map(({ mode, label }) => {
                const selected =
                  mode === 'groups' ? filterToGroups : mode === 'students' ? filterToStudents : false;
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleRecipientFilter(mode)}
                    style={{
                      ...styles.nlFilterTag,
                      ...(selected ? styles.nlFilterTagActive : {}),
                      fontFamily: 'inherit',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {filterToGroups && userGroups.length > 0 && (
            <div style={styles.nlGroupCards} role="radiogroup" aria-label="Choose a group">
              {userGroups.map((group) => {
                const isGroupSelected = String(groupFilter) === String(group.id);
                return (
                  <div
                    key={group.id}
                    role="radio"
                    aria-checked={isGroupSelected}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setGroupFilter(String(group.id));
                      }
                    }}
                    onClick={() => setGroupFilter(String(group.id))}
                    style={{
                      ...styles.nlGroupCard,
                      ...(isGroupSelected ? styles.nlGroupCardSelected : styles.nlGroupCardIdle),
                    }}
                  >
                    <div
                      style={{
                        ...styles.nlGroupCardRadioOuter,
                        ...(isGroupSelected ? styles.nlGroupCardRadioOuterSelected : {}),
                      }}
                      aria-hidden
                    >
                      {isGroupSelected ? <div style={styles.nlGroupCardRadioInner} /> : null}
                    </div>
                    <div style={styles.nlGroupCardInfo}>
                      <div style={styles.nlGroupCardTitleRow}>
                        <span style={styles.nlGroupCardTitle}>{group.title}</span>
                        {isGroupSelected ? (
                          <span style={styles.nlGroupCardSelectedBadge}>Selected</span>
                        ) : null}
                      </div>
                      <div style={styles.nlGroupCardMeta}>
                        {group.accounts?.length || 0} members
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.nlGroupCardEdit}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditGroupModal(group);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {filterToGroups && userGroups.length === 0 && (
            <div style={styles.nlGroupsEmptyHint}>
              <p style={styles.nlGroupsEmptyHintTitle}>You have no groups yet.</p>
              <p style={styles.nlGroupsEmptyHintBody}>
                {canViewAllAccounts ? (
                  <>
                    Use the search box above to find people by email or name, select them in the list,
                    then click <strong style={styles.nlGroupsEmptyHintEm}>Create Group</strong> in the
                    Recipients panel (top right). Creating a group saves a reusable list of contacts.
                  </>
                ) : (
                  <>
                    Turn off <strong style={styles.nlGroupsEmptyHintEm}>My Groups</strong> (click again to
                    uncheck), then enter at least {RECIPIENT_SEARCH_MIN_CHARS} characters in search to find
                    people. Add recipients and use{' '}
                    <strong style={styles.nlGroupsEmptyHintEm}>Create Group</strong> to save a list.
                  </>
                )}
              </p>
            </div>
          )}

          {filterToStudents && (
            <div style={styles.nlStudentsBlock}>
              <p style={styles.nlStudentsNote}>
                — Currently SLA East · {NEWSLETTER_RECIPIENT_SCOPE.periodName}. You may further
                filter by association.
              </p>
              {!studentPeriods.some((p) => p.name === NEWSLETTER_RECIPIENT_SCOPE.periodName) && (
                <p style={styles.nlStudentsWarning}>
                  Term not found in Periods; list may be empty until it exists.
                </p>
              )}
              {studentAssociations.length > 0 && (
                <select
                  style={styles.nlAssocSelect}
                  value={associationFilter ? String(associationFilter) : ''}
                  onChange={(e) => setAssociationFilter(e.target.value)}
                  aria-label="Association"
                >
                  <option value="">All associations</option>
                  {studentAssociations.map((assoc) => (
                    <option key={assoc.id} value={String(assoc.id)}>
                      {assoc.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div style={styles.recipientToolbar}>
            <span style={styles.recipientToolbarCount}>{filteredUsers.length} in list</span>
            <span style={styles.recipientToolbarSpacer} />
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={
                capabilitiesLoaded &&
                !canViewAllAccounts &&
                !filterToGroups &&
                !filterToStudents &&
                !recipientSearchMeetsMinLength(searchTerm)
              }
              style={{
                ...styles.recipientToolbarAction,
                ...(capabilitiesLoaded &&
                !canViewAllAccounts &&
                !filterToGroups &&
                !filterToStudents &&
                !recipientSearchMeetsMinLength(searchTerm)
                  ? styles.recipientToolbarActionDisabled
                  : {}),
              }}
              className="select-all-button"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              style={styles.recipientToolbarActionSecondary}
              className="deselect-all-button"
            >
              Deselect list
            </button>
          </div>
        </div>

        {capabilitiesLoaded &&
          !canViewAllAccounts &&
          !filterToGroups &&
          !filterToStudents &&
          !recipientSearchMeetsMinLength(searchTerm) && (
            <p style={styles.nlRecipientGateHint}>
              With no filter on, results stay hidden until search has at least{' '}
              {RECIPIENT_SEARCH_MIN_CHARS} characters (names or emails). Turn on a filter to
              browse that list. Your selection is kept if you already chose recipients.
            </p>
          )}

        <div style={{ ...styles.usersList, ...usersListExtraStyle }}>
          {filteredUsers.map((user) => {
            const display = getUserDisplayName(user);
            const isSelected = isRecipientSelected(user.account_id);

            return (
              <div
                key={user.account_id}
                style={{
                  ...styles.userItem,
                  ...(isSelected ? styles.selectedUserItem : {}),
                }}
                onClick={(e) => {
                  if (e.target.type !== 'checkbox') {
                    handleRecipientToggle(user.account_id);
                  }
                }}
              >
                <div style={styles.userInfo}>
                  <div style={styles.userName}>{display.name}</div>
                  <div style={styles.userDetails}>
                    {display.email && <span style={styles.userEmail}>{display.email}</span>}
                    {display.role && <span style={styles.userRole}>{display.role}</span>}
                    {display.school && <span style={styles.userSchool}>{display.school}</span>}
                  </div>
                </div>
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleRecipientToggle(user.account_id)}
                    style={styles.checkboxInput}
                    className="checkbox-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Check if user is a student (not allowed to create posts)
  const defaultAccessCard = userData?.accesscards?.find(
    ac => ac.accesscard_id === userData.default_accesscard_id
  );
  const userRole = defaultAccessCard?.role;

  if (userRole === 'Student') {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h1 style={styles.accessDeniedTitle}>Access Denied</h1>
          <p style={styles.accessDeniedText}>
            Students are not allowed to create newsletter posts. You can only view posts that have been shared with you.
          </p>
          <Link to="/newsletter-viewer" style={styles.accessDeniedButton}>
            View Received Posts
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <h1 style={styles.title} data-page-title>Newsletter</h1>
          <Link to="/newsletter-viewer" style={styles.myPostsButton}>
            My Posts
          </Link>
        </div>
        <p style={styles.subtitle}>Create and send a post to selected recipients</p>
      </div>
      <form ref={formRef} onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.postCard}>
          <div style={styles.postToolsRow}>
            <button
              type="button"
              onClick={handleSettingsClick}
              style={styles.settingsIconButton}
              title="Post settings"
              aria-label="Post settings"
            >
              ⚙
            </button>
          </div>
          <div style={styles.titleSection}>
            <label style={styles.label}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              style={styles.titleInput}
              maxLength={200}
            />
          </div>
          
          <div style={styles.contentSection}>
            <div style={styles.contentHeader}>
              <label style={styles.label}>Content</label>
            </div>
            <div style={styles.richTextContainer}>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                className="newsletter-quill-editor"
                style={styles.richTextEditor}
                placeholder="Write your post..."
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </div>
          </div>

        </div>

      </form>

      {/* Select Recipients — bottom-left panel (CourseBuilder-style chrome only) */}
      <div
        style={{
          ...styles.nlDeployPanel,
          ...(deployPanelExpanded ? styles.nlDeployPanelExpanded : styles.nlDeployPanelCollapsed),
        }}
      >
        {deployPanelExpanded && (
          <div style={styles.nlDeployContent}>
            {renderRecipientSelector(styles.nlDeployUsersList, { hideSectionTitle: true })}
          </div>
        )}
        <div style={styles.nlDeployHeader}>
          <div
            style={styles.nlDeployHeaderLeft}
            onClick={() => setDeployPanelExpanded(!deployPanelExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDeployPanelExpanded(!deployPanelExpanded);
              }
            }}
            aria-expanded={deployPanelExpanded}
            aria-label={deployPanelExpanded ? 'Collapse Select Recipients' : 'Expand Select Recipients'}
          >
            <span style={styles.nlDeployHeaderTextGroup}>
              <span style={styles.nlDeployTitle}>Select Recipients</span>
              {selectedRecipients.length > 0 ? (
                <span style={styles.nlDeploySubtitle}>{selectedRecipients.length} selected</span>
              ) : null}
            </span>
            <span style={styles.nlDeployToggle}>{deployPanelExpanded ? '−' : '+'}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled={sendBlocked}
        aria-busy={submitting}
        style={{
          ...styles.nlFloatingSendButton,
          ...(sendBlocked ? styles.nlFloatingSendButtonDisabled : {}),
        }}
        onClick={() => {
          if (!sendBlocked && formRef.current) {
            formRef.current.requestSubmit();
          }
        }}
        aria-label="Send post"
      >
        <span style={styles.nlFloatingSendInner}>
          <span>Send 📨</span>
        </span>
      </button>

      {showSettings && (
        <div style={styles.modalOverlay}>
          <div style={styles.settingsModal} data-settings-panel>
            <div style={styles.settingsHeader}>
              <h3 style={styles.settingsTitle}>Post Settings</h3>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                style={styles.closeSettingsButton}
              >
                ×
              </button>
            </div>
            <div style={styles.settingsContent}>
              {[
                ['allowMainResponse', 'Allow Main Response', 'Allow replies directly to this post'],
                ['allowCommentResponse', 'Allow Comment Response', "Allow replies to others' responses"],
                ['notifyMeOnResponses', 'Notify Me on Responses', 'Receive email when someone responds'],
                ['allowAssigneesViewResponses', 'Allow Recipients View Responses', 'Allow recipients to view others responses'],
                ['initialNotification', 'Send Initial Notification', 'Send email when the post is created'],
              ].map(([key, label, description]) => (
                <div key={key} style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <label style={styles.settingLabel}>{label}</label>
                    <p style={styles.settingDescription}>{description}</p>
                  </div>
                  <div
                    style={styles.toggleSwitch}
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }))
                    }
                  >
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                      style={styles.toggleInput}
                    />
                    <div
                      style={{
                        ...styles.toggleSlider,
                        ...(settings[key] ? styles.toggleSliderActive : {}),
                      }}
                    >
                      <div
                        style={{
                          ...styles.toggleSliderCircle,
                          ...(settings[key] ? styles.toggleSliderCircleActive : {}),
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Recipients Container */}
      {selectedRecipients.length > 0 && (
        <div style={styles.stickyRecipientsContainer}>
                     <div style={styles.stickyRecipientsHeader}>
             <div style={styles.stickyRecipientsTitle}>
               <span style={styles.recipientsIcon}>👥</span>
               Recipients ({selectedRecipients.length})
             </div>
             <div style={styles.stickyRecipientsActions}>
               <button
                 type="button"
                 onClick={() => copyEmailsToClipboard(selectedRecipients, allUsers)}
                 style={styles.copyEmailsButton}
                 title="Copy emails to clipboard"
               >
                 {copyFeedback ? copyFeedback : '📋 Copy emails'}
               </button>
               <button
                 type="button"
                 onClick={() => setShowCreateGroupModal(true)}
                 style={styles.createGroupButton}
                 className="create-group-button"
                 disabled={selectedRecipients.length === 0}
               >
                 <span className="create-group-button-label" style={styles.createGroupButtonLabel}>
                   Create Group
                 </span>
               </button>
               <button
                 type="button"
                 onClick={() => setShowSelectedRecipients(!showSelectedRecipients)}
                 style={styles.minimizeButton}
               >
                 {showSelectedRecipients ? '−' : '+'}
               </button>
             </div>
           </div>
          {showSelectedRecipients && (
            <div style={styles.stickyRecipientsList}>
              {selectedRecipients.map(accountId => {
                const user = allUsers.find(u => u.account_id === accountId);
                if (!user) return null;
                const display = getUserDisplayName(user);
                return (
                  <div key={accountId} style={styles.stickyRecipientChip}>
                    <span style={styles.stickyRecipientName}>{display.name}</span>
                    <span style={styles.stickyRecipientEmail}>{display.email}</span>
                    <button
                      type="button"
                      onClick={() => handleRecipientToggle(accountId)}
                      style={styles.stickyRemoveButton}
                      className="newsletter-recipient-remove"
                      title="Remove from recipients"
                      aria-label={`Remove ${display.name || 'recipient'} from list`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        .newsletter-quill-editor .ql-toolbar {
          border-color: #e5e7eb;
        }
        .newsletter-quill-editor .ql-container {
          min-height: 260px;
          height: calc(100% - 42px);
          border-color: #e5e7eb;
        }
        .newsletter-quill-editor .ql-editor {
          min-height: 260px;
          font-size: 0.95rem;
        }
      `}</style>

       {/* Create Group Modal */}
       {showCreateGroupModal && (
         <div style={styles.modalOverlay}>
           <div style={styles.modal}>
             <div style={styles.modalHeader}>
               <h3 style={styles.modalTitle}>Create New Group</h3>
               <button
                 onClick={() => setShowCreateGroupModal(false)}
                 style={styles.closeModalButton}
               >
                 ×
               </button>
             </div>
             <div style={styles.modalContent}>
               <div style={styles.modalSection}>
                 <label style={styles.modalLabel}>Group Title *</label>
                 <input
                   type="text"
                   value={newGroupTitle}
                   onChange={(e) => setNewGroupTitle(e.target.value)}
                   placeholder="Enter group title..."
                   style={styles.modalInput}
                   maxLength={100}
                 />
               </div>
               <div style={styles.modalSection}>
                <label style={styles.modalLabel}>Members ({selectedRecipients.length})</label>
                 <div style={styles.modalRecipientsList}>
                   {selectedRecipients.map(accountId => {
                     const user = allUsers.find(u => u.account_id === accountId);
                     if (!user) return null;
                     const display = getUserDisplayName(user);
                     return (
                       <div key={accountId} style={styles.modalRecipientItem}>
                         <span style={styles.modalRecipientName}>{display.name}</span>
                         <span style={styles.modalRecipientEmail}>{display.email}</span>
                       </div>
                     );
                   })}
                 </div>
               </div>
             </div>
             <div style={styles.modalActions}>
               <button
                 type="button"
                 onClick={() => setShowCreateGroupModal(false)}
                 style={styles.modalCancelButton}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 onClick={createGroup}
                 disabled={creatingGroup || !newGroupTitle.trim()}
                 style={{
                   ...styles.modalConfirmButton,
                   ...(creatingGroup || !newGroupTitle.trim() ? styles.disabledButton : {})
                 }}
               >
                 {creatingGroup ? 'Creating...' : 'Create Group'}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Edit Group Modal */}
       {showEditGroupModal && (
         <div style={styles.modalOverlay}>
           <div style={styles.editGroupModal}>
             <div style={styles.modalHeader}>
               <h3 style={styles.modalTitle}>Edit Group</h3>
               <button
                 onClick={() => {
                   setShowEditGroupModal(false);
                   setEditingGroup(null);
                   setEditGroupTitle('');
                   setEditGroupAccounts([]);
                   setEditGroupSearchTerm('');
                 }}
                 style={styles.closeModalButton}
               >
                 ×
               </button>
             </div>
             <div style={styles.modalContent}>
               <div style={styles.modalSection}>
                 <label style={styles.modalLabel}>Group Title *</label>
                 <input
                   type="text"
                   value={editGroupTitle}
                   onChange={(e) => setEditGroupTitle(e.target.value)}
                   placeholder="Enter group title..."
                   style={styles.modalInput}
                   maxLength={100}
                 />
               </div>
               <div style={styles.twoColumnContainer}>
                 <div style={styles.leftColumn}>
                   <div style={styles.columnHeader}>
                     <label style={styles.columnLabel}>All Users ({filteredEditUsers.length})</label>
                     <textarea
                       placeholder="Search users by name, email, role, or school...&#10;Enter multiple terms separated by commas, semicolons, or new lines"
                       value={editGroupSearchTerm}
                       onChange={(e) => setEditGroupSearchTerm(e.target.value)}
                       style={styles.columnSearchTextarea}
                       rows={3}
                     />
                     {editGroupSearchTerm.trim() && (
                       <div style={styles.editGroupSearchInfo}>
                         Searching for: {editGroupSearchTerm.split(/[\n,;]+/).filter(term => term.trim()).length} term(s)
                       </div>
                     )}
                     <div style={styles.editGroupSelectAllRow}>
                       <button
                         type="button"
                         onClick={() => {
                           // Add all filtered users to the group
                           const newAccountIds = [...new Set([...editGroupAccounts, ...filteredEditUsers.map(user => user.account_id)])];
                           setEditGroupAccounts(newAccountIds);
                         }}
                         style={styles.editGroupSelectAllButton}
                         className="edit-group-select-all-button"
                       >
                         Select All ({filteredEditUsers.length})
                       </button>
                       <button
                         type="button"
                         onClick={() => {
                           // Remove all filtered users from the group
                           const filteredUserIds = filteredEditUsers.map(user => user.account_id);
                           const newAccountIds = editGroupAccounts.filter(id => !filteredUserIds.includes(id));
                           setEditGroupAccounts(newAccountIds);
                         }}
                         style={styles.editGroupDeselectAllButton}
                         className="edit-group-deselect-all-button"
                       >
                         Deselect All
                       </button>
                     </div>
                   </div>
                   <div style={styles.columnUsersList}>
                     {filteredEditUsers.map(user => {
                       const display = getUserDisplayName(user);
                       const isSelected = editGroupAccounts.includes(user.account_id);
                       return (
                         <div
                           key={user.account_id}
                           style={{
                             ...styles.compactUserItem,
                             ...(isSelected ? styles.selectedCompactUserItem : {})
                           }}
                           onClick={() => handleEditGroupAccountToggle(user.account_id)}
                         >
                           <div style={styles.compactUserInfo}>
                             <span style={styles.compactUserName}>{display.name}</span>
                             <span style={styles.compactUserEmail}>{display.email}</span>
                             {display.role && <span style={styles.compactUserRole}>{display.role}</span>}
                             {display.school && <span style={styles.compactUserSchool}>{display.school}</span>}
                           </div>
                           <div style={styles.compactCheckbox}>
                             <input
                               type="checkbox"
                               checked={isSelected}
                               onChange={() => handleEditGroupAccountToggle(user.account_id)}
                               style={styles.compactCheckboxInput}
                               onClick={(e) => e.stopPropagation()}
                             />
                           </div>
                         </div>
                       );
                     })}
                   </div>
                 </div>
                 <div style={styles.rightColumn}>
                   <div style={styles.columnHeader}>
                     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                       <label style={styles.columnLabel}>Group Members ({editGroupAccounts.length})</label>
                       {editGroupAccounts.length > 0 && (
                         <button
                           type="button"
                           onClick={() => copyEmailsToClipboard(editGroupAccounts, allUsers)}
                           style={styles.copyEmailsButton}
                           title="Copy emails to clipboard"
                         >
                           {copyFeedback ? copyFeedback : '📋 Copy emails'}
                         </button>
                       )}
                     </div>
                   </div>
                   <div style={styles.columnUsersList}>
                     {editGroupAccounts.length === 0 ? (
                       <div style={styles.emptyColumnMessage}>
                         No members selected
                       </div>
                     ) : (
                       editGroupAccounts.map(accountId => {
                         const user = allUsers.find(u => u.account_id === accountId);
                         if (!user) return null;
                         const display = getUserDisplayName(user);
                         return (
                           <div key={accountId} style={styles.compactUserItem}>
                             <div style={styles.compactUserInfo}>
                               <span style={styles.compactUserName}>{display.name}</span>
                               <span style={styles.compactUserEmail}>{display.email}</span>
                               {display.role && <span style={styles.compactUserRole}>{display.role}</span>}
                               {display.school && <span style={styles.compactUserSchool}>{display.school}</span>}
                             </div>
                             <button
                               onClick={() => handleEditGroupAccountToggle(accountId)}
                               style={styles.compactRemoveButton}
                             >
                               ×
                             </button>
                           </div>
                         );
                       })
                     )}
                   </div>
                 </div>
               </div>
             </div>
             <div style={styles.modalActions}>
               <button
                 type="button"
                 onClick={() => {
                   setShowEditGroupModal(false);
                   setEditingGroup(null);
                   setEditGroupTitle('');
                   setEditGroupAccounts([]);
                   setEditGroupSearchTerm('');
                 }}
                 style={styles.modalCancelButton}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 onClick={editGroup}
                 disabled={updatingGroup || !editGroupTitle.trim()}
                 style={{
                   ...styles.modalConfirmButton,
                   ...(updatingGroup || !editGroupTitle.trim() ? styles.disabledButton : {})
                 }}
               >
                 {updatingGroup ? 'Updating...' : 'Update Group'}
               </button>
             </div>
           </div>
         </div>
       )}

       <PolymorphicAlertOverlay
         open={alertConfig.open}
         onClose={closeAlert}
         variant={alertConfig.variant}
         title={alertConfig.title}
         message={alertConfig.message}
         closeOnOverlayClick={alertConfig.closeOnOverlayClick}
         showCloseButton={alertConfig.showCloseButton}
         autoCloseMs={alertConfig.autoCloseMs}
         closeOnEscape={alertConfig.closeOnEscape}
         zIndex={10000}
       />

       {criticalLoadError && (
         <PolymorphicAlertOverlay
           open
           variant="error"
           title="Newsletter cannot load"
           closeOnOverlayClick={false}
           showCloseButton={false}
           closeOnEscape={false}
           autoCloseMs={null}
           zIndex={20000}
         >
           <div
             style={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               gap: 20,
               width: '100%',
             }}
           >
             <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.55, color: '#334155' }}>
               {criticalLoadError.message}
             </p>
             <button
               type="button"
               onClick={() => window.location.reload()}
               style={{
                 padding: '0.55rem 1.25rem',
                 borderRadius: 8,
                 border: 'none',
                 background: '#1D4E89',
                 color: '#fff',
                 fontSize: '0.95rem',
                 fontWeight: 600,
                 cursor: 'pointer',
               }}
             >
               Reload page
             </button>
           </div>
         </PolymorphicAlertOverlay>
       )}

       {/* Delete Confirmation Modal */}
       {showDeleteConfirmModal && (
         <div style={styles.modalOverlay}>
           <div style={styles.modal}>
             <div style={styles.modalHeader}>
               <h3 style={styles.modalTitle}>Delete Group</h3>
               <button
                 onClick={() => {
                   setShowDeleteConfirmModal(false);
                   setGroupToDelete(null);
                 }}
                 style={styles.closeModalButton}
               >
                 ×
               </button>
             </div>
             <div style={styles.modalContent}>
               <div style={styles.modalSection}>
                 <p style={styles.deleteConfirmText}>
                   Are you sure you want to delete the group "{groupToDelete?.title}"? 
                   This action cannot be undone.
                 </p>
                 {groupToDelete?.accounts?.length > 0 && (
                   <div style={styles.deleteConfirmMembers}>
                     <strong>This group contains {groupToDelete.accounts.length} members:</strong>
                     <div style={styles.deleteConfirmMembersList}>
                       {groupToDelete.accounts.slice(0, 5).map(accountId => {
                         const user = allUsers.find(u => u.account_id === accountId);
                         if (!user) return null;
                         const display = getUserDisplayName(user);
                         return (
                           <div key={accountId} style={styles.deleteConfirmMemberItem}>
                             {display.name} ({display.email})
                           </div>
                         );
                       })}
                       {groupToDelete.accounts.length > 5 && (
                         <div style={styles.deleteConfirmMoreMembers}>
                           ... and {groupToDelete.accounts.length - 5} more
                         </div>
                       )}
                     </div>
                   </div>
                 )}
               </div>
             </div>
             <div style={styles.modalActions}>
               <button
                 type="button"
                 onClick={() => {
                   setShowDeleteConfirmModal(false);
                   setGroupToDelete(null);
                 }}
                 style={styles.modalCancelButton}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 onClick={deleteGroup}
                 style={styles.modalDeleteButton}
               >
                 Delete Group
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

const styles = {
  container: {
    maxWidth: '1080px',
    margin: '0 auto',
    padding: '1.25rem',
    backgroundColor: '#f8fafc',
    minHeight: 'calc(100vh - 64px)'
  },
  header: {
    marginBottom: '1rem'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.35rem'
  },
  myPostsButton: {
    display: 'inline-block',
    padding: '0.35rem 0.7rem',
    backgroundColor: 'transparent',
    color: '#475569',
    textDecoration: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '0.78rem',
    border: '1px solid #e2e8f0',
    transition: 'background-color 0.2s, border-color 0.2s'
  },
  title: {
    color: '#0f172a',
    fontSize: '1.45rem',
    margin: 0,
    fontWeight: '600'
  },
  subtitle: {
    color: '#64748b',
    fontSize: '0.86rem',
    margin: 0
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.85rem'
  },
  postCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1rem',
    border: '1px solid #e5e7eb',
  },
  recipientsCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1rem',
    border: '1px solid #e5e7eb',
  },
  nlDeployPanel: {
    position: 'fixed',
    bottom: 20,
    left: 20,
    zIndex: 1000,
    backgroundColor: 'white',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column-reverse',
    width: 'clamp(600px, 64vw, 840px)',
    maxWidth: 'calc(100vw - 40px)',
  },
  nlDeployPanelCollapsed: {
    maxHeight: 56,
    overflow: 'hidden',
    width: 'auto',
    minWidth: 'min(100vw - 40px, 520px)',
  },
  nlDeployPanelExpanded: {
    maxHeight: '80vh',
    overflow: 'hidden',
    width: 'clamp(600px, 64vw, 840px)',
    maxWidth: 'calc(100vw - 40px)',
  },
  nlDeployHeader: {
    display: 'flex',
    justifyContent: 'stretch',
    alignItems: 'center',
    padding: '14px 18px',
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    userSelect: 'none',
  },
  nlDeployHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
    cursor: 'pointer',
    padding: '4px 8px',
    margin: '-4px -8px',
    borderRadius: 6,
  },
  nlDeployHeaderTextGroup: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0.35rem',
    minWidth: 0,
  },
  nlDeployTitle: {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: 14,
    whiteSpace: 'nowrap',
  },
  nlDeploySubtitle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#64748b',
    whiteSpace: 'nowrap',
  },
  nlDeployToggle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#64748b',
    flexShrink: 0,
  },
  nlDeployContent: {
    padding: '16px 16px 12px',
    overflowY: 'auto',
    maxHeight: 'calc(80vh - 56px)',
    borderBottom: '1px solid #e2e8f0',
  },
  nlDeployUsersList: {
    maxHeight: 'min(320px, 38vh)',
  },
  nlFloatingSendButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 1010,
    padding: '0.8rem 1.5rem',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 999,
    fontSize: '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(37, 99, 235, 0.45), 0 2px 8px rgba(15, 23, 42, 0.12)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nlFloatingSendButtonDisabled: {
    background: '#94a3b8',
    cursor: 'not-allowed',
    boxShadow: '0 2px 12px rgba(15, 23, 42, 0.1)',
    filter: 'none',
  },
  nlFloatingSendInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  },
  nlFloatingSendArrow: {
    display: 'block',
    flexShrink: 0,
    opacity: 0.95,
  },
  nlGroupsEmptyHint: {
    marginTop: 8,
    marginBottom: '0.5rem',
    padding: '0.65rem 0.75rem',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
  nlGroupsEmptyHintTitle: {
    margin: '0 0 0.4rem 0',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#334155',
  },
  nlGroupsEmptyHintBody: {
    margin: 0,
    fontSize: '0.75rem',
    color: '#64748b',
    lineHeight: 1.55,
  },
  nlGroupsEmptyHintEm: {
    color: '#1e40af',
    fontWeight: 600,
  },
  titleSection: {
    marginBottom: '0.85rem'
  },
  postToolsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.35rem'
  },
  settingsIconButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    color: '#64748b',
    fontSize: '1rem',
    lineHeight: 1
  },
  titleInput: {
    width: '100%',
    padding: '0.6rem 0.7rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.95rem',
    outline: 'none',
    fontFamily: 'inherit'
  },
  contentSection: {
    marginBottom: 0
  },
  contentHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '0.5rem'
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#374151',
    marginBottom: '0.4rem'
  },
  richTextContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  richTextEditor: {
    height: '330px'
  },
  recipientsSection: {
    marginBottom: 0
  },
  recipientsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.65rem'
  },
  recipientsHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem'
  },
  selectedRecipients: {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd'
  },
  selectedTitle: {
    margin: '0 0 0.5rem 0',
    color: '#1e40af',
    fontSize: '1rem',
    fontWeight: '600'
  },
  recipientsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  recipientChip: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.875rem',
    border: '1px solid #93c5fd'
  },
  recipientName: {
    fontWeight: '500',
    marginRight: '0.5rem'
  },
  recipientEmail: {
    color: '#6b7280',
    marginRight: '0.5rem'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    padding: '0',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  recipientSelector: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  recipientFilterShell: {
    padding: '0.6rem 0.75rem 0',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e5e7eb',
  },
  nlSearchLabel: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  nlSearchHint: {
    color: '#94a3b8',
    fontWeight: 400,
    fontSize: '0.72rem',
    marginLeft: 6,
  },
  nlRecipientGateHint: {
    margin: '0 0.75rem 0.5rem',
    padding: '0.5rem 0.65rem',
    fontSize: '0.78rem',
    lineHeight: 1.45,
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    border: '1px solid #e2e8f0',
  },
  nlFilterToRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 1rem',
    marginTop: '0.75rem',
    marginBottom: 4,
  },
  nlFilterToPrefix: {
    fontSize: '0.8125rem',
    color: '#64748b',
    flexShrink: 0,
  },
  nlFilterTagGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  nlFilterTag: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: '6px 14px',
    borderRadius: 999,
    fontSize: '0.8125rem',
    fontWeight: 500,
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#64748b',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    userSelect: 'none',
  },
  nlFilterTagActive: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(37, 99, 235, 0.08)',
  },
  nlGroupCards: {
    marginTop: 8,
    marginBottom: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  nlGroupCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    borderRadius: 8,
    padding: '0.5rem 0.65rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
  },
  nlGroupCardIdle: {
    border: '2px solid #e2e8f0',
    backgroundColor: '#ffffff',
    boxShadow: 'none',
  },
  nlGroupCardSelected: {
    border: '2px solid #2563eb',
    backgroundColor: '#eff6ff',
    boxShadow: '0 1px 3px rgba(37, 99, 235, 0.12)',
  },
  nlGroupCardRadioOuter: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid #cbd5e1',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  nlGroupCardRadioOuterSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  nlGroupCardRadioInner: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#2563eb',
  },
  nlGroupCardInfo: {
    minWidth: 0,
    flex: 1,
  },
  nlGroupCardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  nlGroupCardTitle: {
    color: '#1e293b',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  nlGroupCardSelectedBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#1d4ed8',
    backgroundColor: '#dbeafe',
    padding: '2px 6px',
    borderRadius: 4,
  },
  nlGroupCardMeta: {
    color: '#94a3b8',
    fontSize: '0.74rem',
  },
  nlGroupCardEdit: {
    padding: '0.25rem 0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    backgroundColor: '#fff',
    color: '#475569',
    fontSize: '0.72rem',
    cursor: 'pointer',
  },
  nlStudentsBlock: {
    marginTop: 8,
    marginBottom: '0.65rem',
  },
  nlStudentsNote: {
    margin: '0 0 0.45rem 0',
    fontSize: '0.75rem',
    color: '#64748b',
    lineHeight: 1.45,
  },
  nlStudentsWarning: {
    margin: '0 0 0.45rem 0',
    fontSize: '0.72rem',
    color: '#b45309',
    lineHeight: 1.35,
  },
  nlAssocSelect: {
    width: '100%',
    maxWidth: 320,
    padding: '6px 8px',
    fontSize: '0.8125rem',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    color: '#334155',
  },
  recipientToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 0.75rem',
    padding: '0.65rem 0 0.75rem',
    marginBottom: 0,
  },
  recipientToolbarCount: {
    fontSize: '0.75rem',
    color: '#64748b',
    fontWeight: 500,
  },
  recipientToolbarSpacer: {
    flex: 1,
    minWidth: 8,
  },
  recipientToolbarAction: {
    padding: '0.28rem 0.65rem',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 600,
    transition: 'all 0.15s ease',
  },
  recipientToolbarActionDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  recipientToolbarActionSecondary: {
    padding: '0.28rem 0.65rem',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
    transition: 'all 0.15s ease',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '1rem',
    outline: 'none'
  },
  searchTextarea: {
    width: '100%',
    padding: '0.5rem 0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '52px'
  },
  usersList: {
    maxHeight: '400px',
    overflowY: 'auto',
    backgroundColor: '#fff'
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.65rem 0.75rem',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    backgroundColor: '#fff'
  },
  selectedUserItem: {
    backgroundColor: '#f8fafc',
    borderLeft: '2px solid #cbd5e1'
  },
  userInfo: {
    flex: 1
  },
  userName: {
    fontWeight: '500',
    color: '#334155',
    marginBottom: '0.18rem',
    fontSize: '0.86rem'
  },
  userDetails: {
    display: 'flex',
    gap: '0.45rem',
    flexWrap: 'wrap',
    fontSize: '0.76rem',
    color: '#6b7280'
  },
  userEmail: {
    color: '#64748b'
  },
  userRole: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    padding: '0.08rem 0.38rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    border: '1px solid #e2e8f0'
  },
  userSchool: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    padding: '0.08rem 0.38rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    border: '1px solid #e2e8f0'
  },
  checkbox: {
    marginLeft: '1rem'
  },
  checkboxInput: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  submitSection: {
    textAlign: 'center'
  },
  submitButton: {
    padding: '0.7rem 1.15rem',
    backgroundColor: '#1f2937',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  disabledButton: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed'
  },
  accessDenied: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    margin: '0 auto'
  },
  accessDeniedTitle: {
    color: '#dc2626',
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: '600'
  },
  accessDeniedText: {
    color: '#6b7280',
    fontSize: '1.1rem',
    marginBottom: '2rem',
    lineHeight: '1.6'
  },
  accessDeniedButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#2563eb',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '1rem',
    transition: 'background-color 0.2s'
  },
  stickyRecipientsContainer: {
    position: 'fixed',
    top: '112px',
    right: '1rem',
    zIndex: 900,
    width: '60%',
    maxWidth: '640px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
  },
  stickyRecipientsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.65rem',
    backgroundColor: '#fcfcfd',
    borderBottom: '1px solid #e5e7eb',
    borderRadius: '8px 8px 0 0'
  },
  stickyRecipientsActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  copyEmailsButton: {
    padding: '0.24rem 0.52rem',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.72rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease'
  },
  createGroupButton: {
    padding: '0.28rem 0.6rem',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #93c5fd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.72rem',
    fontWeight: 600,
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  },
  createGroupButtonLabel: {
    color: '#1d4ed8',
    fontWeight: 600,
  },
  stickyRecipientsTitle: {
    fontWeight: '500',
    color: '#475569',
    fontSize: '0.84rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem'
  },
  recipientsIcon: {
    fontSize: '0.95rem',
    opacity: 0.75
  },
  minimizeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem',
    borderRadius: '4px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  stickyRecipientsList: {
    padding: '0.55rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.35rem',
    maxHeight: '150px',
    overflowY: 'auto'
  },
  stickyRecipientChip: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    color: '#475569',
    padding: '0.18rem 0.45rem',
    borderRadius: '999px',
    fontSize: '0.74rem',
    border: '1px solid #e2e8f0',
    maxWidth: '300px'
  },
  stickyRecipientName: {
    fontWeight: '500',
    marginRight: '0.3rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  stickyRecipientEmail: {
    color: '#6b7280',
    marginRight: '0.3rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.72rem'
  },
  stickyRemoveButton: {
    background: 'none',
    border: 'none',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '1.05rem',
    fontWeight: 'bold',
    lineHeight: 1,
    padding: '0 2px',
    minWidth: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: '4px',
  },
  settingsButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  settingsPanel: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1rem',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    marginBottom: '1rem',
    border: '1px solid #e5e7eb'
  },
  settingsModal: {
    backgroundColor: 'white',
    borderRadius: '14px',
    maxWidth: '720px',
    width: '92%',
    maxHeight: '82vh',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    boxShadow: '0 24px 48px rgba(2, 6, 23, 0.22)',
  },
  settingsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    padding: '0.95rem 1rem 0.75rem',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#fafcff'
  },
  settingsTitle: {
    color: '#0f172a',
    fontSize: '0.96rem',
    fontWeight: '600',
    margin: 0
  },
  closeSettingsButton: {
    background: 'none',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    color: '#64748b',
    cursor: 'pointer',
    padding: '0',
    borderRadius: '6px',
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  settingsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    padding: '0.9rem 1rem 1rem',
    maxHeight: '65vh',
    overflowY: 'auto'
  },
  settingItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.62rem 0.7rem',
    backgroundColor: '#fcfcfd',
    borderRadius: '8px',
    border: '1px solid #eef2f7'
  },
  settingInfo: {
    flex: 1
  },
  settingLabel: {
    fontWeight: '600',
    fontSize: '0.85rem',
    color: '#374151',
    marginBottom: '0.25rem'
  },
  settingDescription: {
    fontSize: '0.75rem',
    color: '#6b7280',
    margin: 0
  },
  toggleSwitch: {
    position: 'relative',
    width: '40px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
    position: 'absolute',
    zIndex: -1
  },
  toggleSlider: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#e5e7eb',
    borderRadius: '20px',
    transition: 'background-color 0.3s'
  },
  toggleSliderActive: {
    backgroundColor: '#2563eb'
  },
  toggleSliderCircle: {
    position: 'absolute',
    width: '16px',
    height: '16px',
    left: '2px',
    top: '2px',
    borderRadius: '50%',
    backgroundColor: 'white',
    transition: 'transform 0.3s'
  },
  toggleSliderCircleActive: {
    transform: 'translateX(20px)'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '0',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb'
  },
  modalTitle: {
    color: '#1D4E89',
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: 0
  },
  closeModalButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  modalContent: {
    padding: '1.5rem',
    maxHeight: '60vh',
    overflowY: 'auto'
  },
  modalSection: {
    marginBottom: '1.5rem'
  },
  modalLabel: {
    display: 'block',
    fontWeight: '600',
    fontSize: '1rem',
    color: '#374151',
    marginBottom: '0.5rem'
  },
  modalInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '1rem',
    outline: 'none',
    fontFamily: 'inherit'
  },
  modalRecipientsList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0.5rem'
  },
  modalRecipientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.875rem'
  },
  modalRecipientName: {
    fontWeight: '500',
    color: '#374151'
  },
  modalRecipientEmail: {
    color: '#6b7280',
    fontSize: '0.8rem'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1.5rem',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  modalCancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  modalConfirmButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  editGroupModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '0',
    maxWidth: 'min(95vw, 1400px)',
    width: 'min(95vw, 1400px)',
    minWidth: '600px',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  twoColumnContainer: {
    display: 'flex',
    gap: '1rem',
    height: '400px'
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  rightColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  columnHeader: {
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  columnLabel: {
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#374151',
    margin: 0
  },
  columnSearchInput: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.875rem',
    outline: 'none'
  },
  columnSearchTextarea: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px'
  },
  editGroupSearchInfo: {
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '0.25rem',
    fontStyle: 'italic'
  },
  editGroupSelectAllRow: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px solid #e5e7eb'
  },
  editGroupSelectAllButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '500',
    transition: 'all 0.2s',
    flex: 1
  },
  editGroupDeselectAllButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '500',
    transition: 'all 0.2s',
    flex: 1
  },
  columnUsersList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.5rem'
  },
  emptyColumnMessage: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.875rem',
    padding: '2rem 1rem',
    fontStyle: 'italic'
  },
  compactUserItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    borderRadius: '4px',
    marginBottom: '0.25rem'
  },
  selectedCompactUserItem: {
    backgroundColor: '#eff6ff',
    borderLeft: '3px solid #2563eb'
  },
  compactUserInfo: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    minWidth: 0
  },
  compactUserName: {
    fontWeight: '600',
    color: '#374151',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '120px',
    flex: '0 0 auto'
  },
  compactUserEmail: {
    color: '#059669',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '150px',
    flex: '1 1 auto'
  },
  compactUserRole: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '0.125rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    flex: '0 0 auto'
  },
  compactUserSchool: {
    backgroundColor: '#e0e7ff',
    color: '#3730a3',
    padding: '0.125rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    flex: '0 0 auto'
  },
  compactCheckbox: {
    marginLeft: '0.5rem',
    flexShrink: 0
  },
  compactCheckboxInput: {
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  },
  compactRemoveButton: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    padding: '0',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: '50%',
    transition: 'background-color 0.2s'
  },
  modalUsersList: {
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px'
  },
  modalUserItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  selectedModalUserItem: {
    backgroundColor: '#eff6ff',
    borderLeft: '4px solid #2563eb'
  },
  modalUserInfo: {
    flex: 1
  },
  modalUserName: {
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.25rem'
  },
  modalUserDetails: {
    display: 'flex',
    gap: '0.75rem',
    fontSize: '0.875rem',
    color: '#6b7280'
  },
  modalUserEmail: {
    color: '#059669'
  },
  modalUserRole: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  modalUserSchool: {
    backgroundColor: '#e0e7ff',
    color: '#3730a3',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  modalCheckbox: {
    marginLeft: '1rem'
  },
  modalCheckboxInput: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  modalDeleteButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  deleteConfirmText: {
    fontSize: '1rem',
    color: '#374151',
    marginBottom: '1rem',
    lineHeight: '1.5'
  },
  deleteConfirmMembers: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '1rem'
  },
  deleteConfirmMembersList: {
    marginTop: '0.5rem'
  },
  deleteConfirmMemberItem: {
    fontSize: '0.875rem',
    color: '#374151',
    marginBottom: '0.25rem'
  },
  deleteConfirmMoreMembers: {
    fontSize: '0.875rem',
    color: '#6b7280',
    fontStyle: 'italic'
  }
};

// Add CSS styles for better UI interactions
const newsletterStyles = `
  /* Subtle button hover effects */
  .subtle-button {
    transition: all 0.2s ease;
  }
  
  .subtle-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #374151 !important;
  }
  
  /* Select All/Deselect All button hover effects */
  .select-all-button:hover,
  .deselect-all-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #374151 !important;
  }
  
  /* Checkbox styling */
  .checkbox-input {
    cursor: pointer;
  }
  
  .checkbox-input:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
  
  /* Create Group button hover / disabled (Recipients panel) */
  .create-group-button:not(:disabled):hover {
    background-color: #dbeafe !important;
    border-color: #60a5fa !important;
    color: #1e40af !important;
  }
  .create-group-button:not(:disabled):hover .create-group-button-label {
    color: #1e40af !important;
  }
  .create-group-button:disabled {
    background-color: #f1f5f9 !important;
    border-color: #e2e8f0 !important;
    color: #94a3b8 !important;
    cursor: not-allowed !important;
  }
  .create-group-button:disabled .create-group-button-label {
    color: #94a3b8 !important;
  }

  .newsletter-recipient-remove:hover {
    color: #b91c1c !important;
    background-color: #fef2f2 !important;
  }
  .newsletter-recipient-remove:focus-visible {
    outline: 2px solid #dc2626;
    outline-offset: 1px;
  }
  
  /* Edit Group Select/Deselect All button hover effects */
  .edit-group-select-all-button:hover,
  .edit-group-deselect-all-button:hover {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #374151 !important;
  }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = newsletterStyles;
  document.head.appendChild(styleElement);
}

export default Newsletter; 